import { AxiosInstance } from "axios";
import { toast } from "react-toastify";
import { apiURL } from "../../config";

export type GroupType = "decision" | "user";

export type UserInput = {
	email: string;
	firstName: string;
	lastName: string;
	roleInOrganization: string;
	phone: string;
	website: string;
	officeAddress: string;
	privateAddress: string;
	about: string;
	username: string;
	password: string;
	roles: string[];
};

export interface UserType {
	isAdmin: boolean;
	id: number;
	username: string;
	email: string;
	firstName: string;
	lastName: string;
	roles: string[];
	phone: string;
	active: boolean;
	organizationUnits: OrganizationUnit[];
}

export interface OrganizationUnit {
	organizationUnit: OrganizationUnit2;
	roles: string[];
}

export interface OrganizationUnit2 {
	id: number;
	name: string;
}

export async function getUser(axiosPrivate: AxiosInstance, userId: number) {
	return axiosPrivate
		.get(`${apiURL}/admin/user/${userId}.json`)
		.then((res) => {
			if (res.status < 300 && res.status > 199) return res.data;
		})
		.catch((err) => {
			console.error(err);
			return Promise.reject(err);
		});
}

export async function getUsers(axiosPrivate: AxiosInstance, phrase = "", limit = 100, offset = 0) {
	return axiosPrivate
		.get(`${apiURL}/admin/user.json?phrase=${phrase}&limit=${limit}&offset=${offset}`)
		.then((res) => {
			if (res.status < 300 && res.status > 199) return res.data;
		})
		.catch((err) => {
			console.error(err);
			return Promise.reject(err);
		});
}

export async function postCreateUser(variables: { axiosPrivate: AxiosInstance; data: UserInput }) {
	const { axiosPrivate, data } = variables;
	return axiosPrivate
		.post(`${apiURL}/admin/user.json`, data)
		.then((res) => {
			if (res.status < 300 && res.status > 199) {
				toast.success("User created");
				return res;
			} else {
				toast.error("Could not create user. Please try again.");
			}
		})
		.catch((err) => {
			console.error(err);
			// Validation errors (400) are shown in the form; show other API errors (e.g. 409) as a toast
			if (err.response?.status !== 400) toast.error(err.response?.data?.message || "User - failed to create");
			return err.response;
		});
}

export type ChangeUserPasswordInputType = {
	userId: number;
	newPassword: string;
};

export async function postChangeUserPassword(variables: { axiosPrivate: AxiosInstance; data: ChangeUserPasswordInputType }) {
	const { axiosPrivate, data } = variables;
	return axiosPrivate
		.post(`${apiURL}/admin/user/change-password/${data.userId}.json`, { newPassword: data.newPassword })
		.then((res) => {
			if (res.status < 300 && res.status > 199) {
				toast.success("User password updated");
				return res.data;
			} else {
				toast.error("Could not change user password. Please try again.");
			}
		})
		.catch((err) => {
			console.error(err);
			return err.response;
		});
}

export type UserProfileInput = {
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
};

// Only the fields that are sent are changed. Validation errors (400) are returned for the form.
export async function updateUserProfile(variables: { axiosPrivate: AxiosInstance; userId: number; data: Partial<UserProfileInput> }) {
	const { axiosPrivate, userId, data } = variables;
	return axiosPrivate
		.patch(`${apiURL}/admin/user/${userId}/profile.json`, data)
		.then((res) => {
			toast.success("User - updated");
			return res;
		})
		.catch((err) => {
			if (err.response?.status !== 400) toast.error(err.response?.data?.message || "User - failed to update");
			return err.response;
		});
}

// Organization role: ROLE_ADMIN or ROLE_USER. A 409 means the organization would keep too few admins.
export async function setUserRoles(variables: { axiosPrivate: AxiosInstance; userId: number; roles: string[] }) {
	const { axiosPrivate, userId, roles } = variables;
	return axiosPrivate
		.patch(`${apiURL}/admin/user/${userId}.json`, { roles })
		.then((res) => {
			toast.success("Role - changed");
			return res;
		})
		.catch((err) => {
			toast.error(err.response?.data?.message || "Role - failed to change");
			return err.response;
		});
}

// Users are never deleted; organization admins deactivate (and reactivate) them
export async function setUserActive(variables: { axiosPrivate: AxiosInstance; userId: number; active: boolean }) {
	const { axiosPrivate, userId, active } = variables;
	return axiosPrivate
		.patch(`${apiURL}/admin/user/${userId}/active.json`, { active })
		.then((res) => {
			if (res.status > 199 && res.status < 300) {
				toast.success(active ? "User reactivated" : "User deactivated");
				return res;
			} else {
				toast.error(active ? "Could not reactivate user. Please try again." : "Could not deactivate user. Please try again.");
			}
		})
		.catch((err) => {
			console.error(err);
			// e.g. 409 when the organization would be left with too few admins
			toast.error(err.response?.data?.message || (active ? "User failed to be reactivated" : "User failed to be deactivated"));
			return err.response;
		});
}
